import type { ReactNode } from "react";

interface Props {
  title: string;
  /** Small uppercase eyebrow above the title — section / domain label. */
  eyebrow?: string;
  description?: string;
  actions?: ReactNode;
}

/**
 * Editorial page header. The title uses Fraunces at a weight that
 * leans into the serif's character (semibold + opsz auto via the
 * Google Fonts variable file). We deliberately do NOT center anything
 * — the eyebrow + title + description form a left-aligned column,
 * actions float right with vertical centering on the title row.
 */
export function PageHeader({ title, eyebrow, description, actions }: Props) {
  // On mobile we stack the title block above the action row — there's
  // not enough horizontal room for both side-by-side, and trying to
  // float actions to the right pushes them off-screen. The action
  // row is also `flex-wrap` so multi-button toolbars (e.g. the movie
  // detail page with Trailer / Monitor / Search / Remove) wrap onto
  // a second line cleanly instead of horizontally overflowing.
  return (
    <header className="border-b border-line pb-6 pt-2">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div className="min-w-0 flex-1">
          {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
          <h1 className="font-display text-2xl font-semibold leading-[1.05] tracking-display-tight sm:text-3xl md:text-4xl">
            {title}
          </h1>
          {description && (
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground/65">
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
